// The combobox behind every Sprint 4 picker — a supplier, a brand, a product, a tariff code (UC-CATALOGUE-003 A2, A5;
// UC-PURCHASING-003 A2; UC-TENANCY-010 A3). Keyboard-first like the patients' picker: typing opens the list, ArrowDown and ArrowUp
// move, Enter picks, Escape closes; the mouse picks on press. Options come from the server as the user types (`remote`, from
// `minChars` characters, debounced) or from a list already read, filtered here (`local`, open on focus). The markup is the
// prototype's: `.field.typeahead`, `.input`, `.panel.dropdown`, `.list > .item` (design/base.css, app.css).
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Icon } from "./icons";

export type TypeaheadSource<T> =
  | { kind: "remote"; key: readonly unknown[]; search: (q: string) => Promise<T[]>; minChars?: number | undefined }
  | { kind: "local"; items: readonly T[]; match: (item: T, q: string) => boolean };

interface Common<T> {
  id?: string | undefined;
  /** The field's label; `ariaLabel` names a bare toolbar control instead. */
  label?: ReactNode;
  ariaLabel?: string | undefined;
  required?: boolean | undefined;
  error?: string | undefined;
  help?: ReactNode;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  mono?: boolean | undefined;
  source: TypeaheadSource<T>;
  keyOf: (item: T) => string;
  renderOption: (item: T) => ReactNode;
  /** What the open list says when nothing matches. */
  emptyText?: ((q: string) => string) | undefined;
  /** A class for the box the input sits in — a toolbar width (`w-sel`). */
  boxClass?: string | undefined;
  /** Without the field wrapper: a toolbar control. */
  bare?: boolean | undefined;
  /**
   * Leaves out options as they are shown, never in what is read and kept: a rule that changes while the list stands — a product
   * another row picked since — applies to the options already read (TC-CATALOGUE-132).
   */
  filter?: ((item: T) => boolean) | undefined;
  /** At most this many options shown, after `filter`. */
  max?: number | undefined;
}

/** A pick-only control: the input shows the chosen item; typing over it clears the choice and searches again. */
export interface PickProps<T> extends Common<T> {
  value: T | null;
  labelOf: (item: T) => string;
  onPick: (item: T | null) => void;
}

/** A text field with suggestions: the text is the value (a tariff code the list does not hold may still be typed). */
export interface SuggestProps<T> extends Common<T> {
  text: string;
  onText: (text: string) => void;
  onPick: (item: T) => void;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function useOptions<T>(source: TypeaheadSource<T>, typed: string, filter?: (item: T) => boolean, max?: number): { items: T[]; loading: boolean; failed: boolean; ready: boolean } {
  const q = useDebounced(typed.trim(), 200);
  const remote = source.kind === "remote";
  const min = remote ? source.minChars ?? 1 : 0;
  const enabled = remote && q.length >= min;
  const r = useQuery({
    queryKey: remote ? [...source.key, q] : ["typeahead-local"],
    queryFn: () => (remote ? source.search(q) : Promise.resolve([] as T[])),
    enabled, staleTime: 30_000,
  });
  const shown = (items: readonly T[]): T[] => { const kept = filter ? items.filter(filter) : [...items]; return max !== undefined ? kept.slice(0, max) : kept; };
  if (!remote) {
    const t = typed.trim();
    return { items: shown(source.items.filter((i) => t === "" || source.match(i, t))), loading: false, failed: false, ready: true };
  }
  return { items: enabled ? shown(r.data ?? []) : [], loading: enabled && r.isPending, failed: enabled && r.isError, ready: q.length >= min };
}

function Listbox<T>({ id, open, opts, sel, keyOf, renderOption, emptyText, typed, onChoose }: {
  id: string; open: boolean; opts: ReturnType<typeof useOptions<T>>; sel: number; keyOf: (i: T) => string; renderOption: (i: T) => ReactNode;
  emptyText?: ((q: string) => string) | undefined; typed: string; onChoose: (i: T) => void;
}) {
  if (!open || !opts.ready) return null;
  return (
    <div className="panel dropdown" id={id} role="listbox">
      <div className="list">
        {opts.loading && <div className="item"><span className="help">Searching…</span></div>}
        {opts.failed && <div className="item"><span className="help">The search failed; try again.</span></div>}
        {!opts.loading && !opts.failed && opts.items.length === 0 && <div className="item"><span className="help">{emptyText ? emptyText(typed.trim()) : `Nothing matches “${typed.trim()}”.`}</span></div>}
        {opts.items.map((item, i) => (
          <div key={keyOf(item)} className="item clickable" role="option" aria-selected={i === sel} onMouseDown={(e) => { e.preventDefault(); onChoose(item); }}>
            {renderOption(item)}
          </div>
        ))}
      </div>
    </div>
  );
}

function useKeys<T>(items: T[], open: boolean, setOpen: (o: boolean) => void, choose: (i: T) => void) {
  const [sel, setSel] = useState(0);
  useEffect(() => { setSel(0); }, [items.length]);
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") { e.preventDefault(); if (!open) setOpen(true); else setSel((i) => Math.min(i + 1, Math.max(items.length - 1, 0))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { if (open && items[sel]) { e.preventDefault(); choose(items[sel]!); } }
    else if (e.key === "Escape") { if (open) { e.stopPropagation(); setOpen(false); } }
  };
  return { sel, onKeyDown };
}

function useCloseOutside(open: boolean, setOpen: (o: boolean) => void) {
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, setOpen]);
  return wrap;
}

function Frame({ bare, label, required, error, help, inputId, children, wrap }: { bare?: boolean | undefined; label?: ReactNode; required?: boolean | undefined; error?: string | undefined; help?: ReactNode; inputId: string; children: ReactNode; wrap: React.RefObject<HTMLDivElement | null> }) {
  if (bare) return <div className="typeahead" ref={wrap}>{children}</div>;
  return (
    <div className="field typeahead" ref={wrap}>
      {label !== undefined && <label htmlFor={inputId}>{label}{required && <> <span className="req">*</span></>}</label>}
      {children}
      {error && <div className="error" role="alert">{error}</div>}
      {help && !error && <div className="help">{help}</div>}
    </div>
  );
}

/** Choose one item from the options; the chosen item's label stays in the box, with a clear button. */
export function Typeahead<T>(p: PickProps<T>) {
  const auto = useId();
  const inputId = p.id ?? auto;
  const [typed, setTyped] = useState(p.value ? p.labelOf(p.value) : "");
  const [open, setOpen] = useState(false);
  const chosenLabel = p.value ? p.labelOf(p.value) : null;
  // A choice made elsewhere (a reload from the address, a reset) shows in the box; a choice cleared by typing over it keeps
  // what is being typed.
  const typedOver = useRef(false);
  useEffect(() => {
    if (chosenLabel === null && typedOver.current) { typedOver.current = false; return; }
    setTyped(chosenLabel ?? "");
  }, [chosenLabel]);
  const searching = p.value === null ? typed : "";
  const opts = useOptions(p.source, searching, p.filter, p.max);
  const wrap = useCloseOutside(open, setOpen);
  const choose = (item: T) => { p.onPick(item); setTyped(p.labelOf(item)); setOpen(false); };
  const keys = useKeys(opts.items, open && p.value === null, setOpen, choose);
  const listId = `${inputId}-list`;
  return (
    <Frame bare={p.bare} label={p.label} required={p.required} error={p.error} help={p.help} inputId={inputId} wrap={wrap}>
      <div className={`input${p.mono ? " mono" : ""}${p.error ? " err" : ""}${p.disabled ? " disabled" : ""}${p.boxClass ? ` ${p.boxClass}` : ""}`}>
        <Icon name="search" />
        <input id={inputId} type="text" role="combobox" aria-label={p.ariaLabel} aria-expanded={open && p.value === null && opts.ready} aria-controls={listId} aria-autocomplete="list" autoComplete="off"
          placeholder={p.placeholder} value={typed} disabled={p.disabled}
          onChange={(e) => { setTyped(e.target.value); setOpen(true); if (p.value !== null) { typedOver.current = true; p.onPick(null); } }}
          onFocus={() => { if (p.value === null) setOpen(true); }} onKeyDown={keys.onKeyDown} />
        {p.value !== null && !p.disabled && (
          <button className="linkbtn" type="button" aria-label={`Clear ${p.ariaLabel ?? (typeof p.label === "string" ? p.label : "the choice")}`} onClick={() => { p.onPick(null); setTyped(""); }}>Clear</button>
        )}
      </div>
      <Listbox id={listId} open={open && p.value === null} opts={opts} sel={keys.sel} keyOf={p.keyOf} renderOption={p.renderOption} emptyText={p.emptyText} typed={typed} onChoose={choose} />
    </Frame>
  );
}

/** A text field whose suggestions fill it; whatever is typed stays the value. */
export function SuggestInput<T>(p: SuggestProps<T>) {
  const auto = useId();
  const inputId = p.id ?? auto;
  const [open, setOpen] = useState(false);
  const opts = useOptions(p.source, p.text, p.filter, p.max);
  const wrap = useCloseOutside(open, setOpen);
  const choose = (item: T) => { p.onPick(item); setOpen(false); };
  const keys = useKeys(opts.items, open, setOpen, choose);
  const listId = `${inputId}-list`;
  return (
    <Frame bare={p.bare} label={p.label} required={p.required} error={p.error} help={p.help} inputId={inputId} wrap={wrap}>
      <div className={`input${p.mono ? " mono" : ""}${p.error ? " err" : ""}${p.disabled ? " disabled" : ""}${p.boxClass ? ` ${p.boxClass}` : ""}`}>
        <input id={inputId} type="text" role="combobox" aria-label={p.ariaLabel} aria-expanded={open && opts.ready} aria-controls={listId} aria-autocomplete="list" autoComplete="off"
          placeholder={p.placeholder} value={p.text} disabled={p.disabled}
          onChange={(e) => { p.onText(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={keys.onKeyDown} />
      </div>
      <Listbox id={listId} open={open} opts={opts} sel={keys.sel} keyOf={p.keyOf} renderOption={p.renderOption} emptyText={p.emptyText} typed={p.text} onChoose={choose} />
    </Frame>
  );
}
